/* eslint-disable prettier/prettier */
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, HashRouter, Navigate, Outlet } from 'react-router-dom';
import { G, ROUTES, isMobileRoute } from 'enums';
import { DynamicModal, DynamicDrawer, Header, SideBar } from 'components';
import { DesktopOnly } from 'components/layouts/DesktopOnly';
import { useIsMobile } from 'hooks/useIsMobile';
import { SidebarProvider, useSidebar } from 'providers';
import { RouteValue } from 'types';

import * as S from './App.styles';
import { useLocation } from 'react-router';

const MarketTrends = lazy(() => import('./views/MarketTrends'));
const CompetitorAnalysis = lazy(() => import('./views/CompetitorAnalysis'));
const PriceCalculator = lazy(() => import('./views/PriceCalculator'));
const MyStock = lazy(() => import('./views/MyStock'));
const Login = lazy(() => import('./views/Login'));
const Home = lazy(() => import('./views/Home'));
const Support = lazy(() => import('./views/Support'));

/* Views rendered inside the header + sidebar shell. Mobile access is decided by MOBILE_ROUTES in enums/ROUTES.ts */
const SHELL_ROUTES: ReadonlyArray<{ path: RouteValue; View: React.ComponentType }> = [
  { path: ROUTES.home, View: Home },
  { path: ROUTES.marketTrends, View: MarketTrends },
  { path: ROUTES.competitorAnalysis, View: CompetitorAnalysis },
  { path: ROUTES.priceCalculator, View: PriceCalculator },
  { path: ROUTES.myStock, View: MyStock },
  { path: ROUTES.support, View: Support },
];

const Shell = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { isOpen, toggle, close } = useSidebar();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const isBlocked = isMobile && !isMobileRoute(pathname);
  return (
    <>
      <DynamicModal />
      <DynamicDrawer />
      <Header sidebarIsOpen={isOpen} onToggleSidebar={toggle} />
      <S.AppContent>
        {pathname.length > 1 && !isBlocked && (
          <div className="app__siderbar">
            <SideBar sidebarIsOpen={isOpen} onClose={close} />
          </div>
        )}
        <S.AppView>{children}</S.AppView>
      </S.AppContent>
    </>
  );
};

const SideBarAndNavbar = ({ children }: { children: JSX.Element }): JSX.Element => (
  <SidebarProvider>
    <Shell>{children}</Shell>
  </SidebarProvider>
);

const ProtectedRoute = (): JSX.Element => {
  if (!localStorage.getItem(G.token)) return <Navigate to={ROUTES.login} replace />;
  return <Outlet />;
};

export const Router = (): JSX.Element => {
  const STAGING = process?.env?.REACT_APP_STAGING;

  const TheRouter = (STAGING ? HashRouter : BrowserRouter) as typeof BrowserRouter;

  return (
    <Suspense fallback={null}>
      <TheRouter>
        <Routes>
          <Route path={ROUTES.login} element={<Login />} />
          <Route element={<ProtectedRoute />}>
            {SHELL_ROUTES.map(({ path, View }) => (
              <Route
                key={path}
                path={path}
                element={
                  <SideBarAndNavbar>
                    {isMobileRoute(path) ? (
                      <View />
                    ) : (
                      <DesktopOnly>
                        <View />
                      </DesktopOnly>
                    )}
                  </SideBarAndNavbar>
                }
              />
            ))}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TheRouter>
    </Suspense>
  );
};
